import { Fragment, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import {
  FAILURE_RATE_ALERT,
  filteredTotal,
  fileLlmShare,
  summaryFailureRate,
  type IndexMetrics,
} from '@/lib/index-metrics';

import { fetchIndexMetrics } from '../api';
import type { IndexMetricsPage } from '../types';

/** Indexes per page; the server sends one page plus the total and summary. */
const PAGE_SIZE = 50;

/** Wait this long after the last keystroke before searching the server. */
const FILTER_DEBOUNCE_MS = 250;

const pct = (n: number, d: number) => (d > 0 ? `${Math.round((100 * n) / d)}%` : '—');

function StatCard({
  label,
  value,
  sub,
  alert,
}: {
  label: string;
  value: string;
  sub?: string;
  alert?: boolean;
}) {
  return (
    <div className="sheet p-4">
      <p className="text-2xs font-medium uppercase tracking-wider text-dim">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${alert ? 'text-del' : 'text-ink'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-dim">{sub}</p>}
    </div>
  );
}

function describeCodeGraph(g: NonNullable<IndexMetrics['codeGraph']>): string {
  const files = `${g.files} files`;
  const peak = g.peakRssMb ? ` · extractor peaked at ${g.peakRssMb} MB` : '';
  switch (g.status) {
    case 'stored':
      return `${g.triples ?? 0} triples from ${files} (${g.outputMb ?? 0} MB)${peak}`;
    case 'over-cap':
      return `discarded: ${g.outputMb} MB is over CODE_GRAPH_MAX_MB · ${files}${peak}`;
    case 'out-of-memory':
      return `stopped to keep the machine up · ${files}${peak}`;
    case 'skipped':
      return g.basedOn
        ? `skipped: ${g.basedOn.status} on ${g.basedOn.files} files (${g.basedOn.at.slice(0, 10)})`
        : 'skipped';
    case 'store-failed':
      return `extracted ${g.triples ?? 0} triples, but storing them failed`;
    default:
      return `${g.status}${g.files ? ` · ${files}` : ''}`;
  }
}

function Detail({ m }: { m: IndexMetrics }) {
  const f = m.files;
  const s = m.summaries;
  const rules = Object.entries(f.filteredOut)
    .map(([reason, n]) => `${reason} ${n}`)
    .join(', ');
  const rows: Array<[string, string]> = [
    [
      'Collected',
      `${f.collected} files${f.oversized ? ` (+${f.oversized} over size limit, skipped)` : ''}`,
    ],
    ['Filtered by rules', rules || 'none'],
    [
      'Directory triage (LLM)',
      f.dirTriageSkipped ? `${f.dirTriageSkipped} files skipped` : 'not run',
    ],
    ['File triage (LLM)', `${f.llmTriaged} triaged, ${f.llmTriageExcluded} excluded`],
    [
      'Indexed',
      `${f.indexed} files (${f.astParsed} AST-parsed${f.astFailed ? `, ${f.astFailed} AST parse failures` : ''}, ${f.trivial} trivial)`,
    ],
    [
      'File summaries',
      `${s.files.llm} LLM · ${s.files.cached} cached · ${s.files.template} template · ${s.files.failed} failed`,
    ],
    [
      'Symbol summaries',
      `${s.symbols.llm} LLM · ${s.symbols.template} template · ${s.symbols.failed} failed`,
    ],
    ['Directory summaries', `${s.directories.llm} LLM · ${s.directories.failed} failed`],
    [
      'LLM usage',
      `${m.llm.calls} calls · ${(m.llm.promptTokens + m.llm.completionTokens).toLocaleString()} tokens · $${m.llm.cost.toFixed(4)} (${m.model})`,
    ],
  ];
  if (m.docs) {
    rows.push([
      'Docs crawl',
      `${m.docs.source}: ${m.docs.listed} listed → ${m.docs.inScope} in scope → ${m.docs.pagesWritten} written · ${m.docs.otherLanguagePages} other-language pages, ${m.docs.otherLanguageCodeBlocks} code blocks dropped (${m.docs.url})`,
    ]);
  }
  if (m.codeGraph) {
    rows.push(['Code graph', describeCodeGraph(m.codeGraph)]);
  }
  if (m.retrieval) {
    const edges = Object.entries(m.retrieval.edges)
      .map(([k, n]) => `${n} ${k}`)
      .join(', ');
    rows.push([
      'Retrieval index',
      `${m.retrieval.nodes} nodes · ${m.retrieval.prunedNodes} pruned (low importance) · ${m.retrieval.vectors} vectors · edges: ${edges || 'none'}`,
    ]);
  }
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 px-4 py-3 text-xs sm:grid-cols-[12rem_1fr]">
      {rows.map(([k, v]) => (
        <Fragment key={k}>
          <dt className="text-dim">{k}</dt>
          <dd className="font-mono text-ink">{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function IndexMetricsPanel() {
  const { getToken } = useAuth();
  const [data, setData] = useState<IndexMetricsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  // The server searches every index, not just the loaded page; debounce so a
  // typed word is one request, and start the new result set at its first page.
  useEffect(() => {
    const next = filter.trim();
    if (next === query) return;
    const timer = setTimeout(() => {
      setQuery(next);
      setPage(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter, query]);

  useEffect(() => {
    let active = true;
    void (async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const result = await fetchIndexMetrics(token, {
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          query: query || undefined,
        });
        if (active) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load index metrics');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken, page, query]);

  // Indexes can disappear between requests (a package deleted, metrics reset);
  // fall back to the last page that still has rows instead of an empty table.
  const lastPage = data ? Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1) : 0;
  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  if (error)
    return <div className="border border-del/30 bg-del/15 px-4 py-2 text-xs text-del">{error}</div>;
  if (!data) return <div className="py-8 text-center text-sm text-dim">Loading index metrics…</div>;

  const { rows, total, summary } = data;
  const pageStart = page * PAGE_SIZE;

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold text-ink">Index quality</h2>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter packages…"
          aria-label="Filter packages"
          className="w-48 rounded border border-rule bg-bg px-2 py-1 text-xs"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Indexes with metrics"
          value={total.toLocaleString()}
          sub={query ? `matching “${query}”` : 'most recent first'}
        />
        <StatCard
          label="Files summarized by LLM"
          value={
            summary.avgFileLlmShare === null ? '—' : `${Math.round(summary.avgFileLlmShare * 100)}%`
          }
          sub="file + directory summaries, averaged; symbol summaries are mostly templates by design"
        />
        <StatCard
          label="Failing indexes"
          value={String(summary.flagged)}
          sub={`LLM failure rate > ${FAILURE_RATE_ALERT * 100}%`}
          alert={summary.flagged > 0}
        />
        <StatCard
          label="LLM cost"
          value={`$${summary.cost.toFixed(2)}`}
          sub="across these indexes"
        />
      </div>
      {total === 0 ? (
        <p className="text-xs text-dim">
          {query
            ? `No indexes match “${query}”.`
            : 'No index metrics yet — they are recorded for packages indexed from now on.'}
        </p>
      ) : (
        <div
          className={`sheet overflow-x-auto transition-opacity ${isLoading ? 'opacity-60' : ''}`}
          aria-busy={isLoading}
        >
          <table className="w-full text-left text-xs">
            <thead className="text-2xs uppercase tracking-wider text-dim">
              <tr>
                <th className="px-3 py-2">Package</th>
                <th className="px-3 py-2">Indexed</th>
                <th className="px-3 py-2 text-right">Files</th>
                <th className="px-3 py-2 text-right">Filtered</th>
                <th
                  className="px-3 py-2 text-right"
                  title="File and directory summaries written by the LLM"
                >
                  Files by LLM
                </th>
                <th className="px-3 py-2 text-right">Failed</th>
                <th className="px-3 py-2 text-right">Docs</th>
                <th className="px-3 py-2 text-right">Vectors</th>
                <th className="px-3 py-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = r.index_metrics;
                const rate = summaryFailureRate(m);
                const share = fileLlmShare(m);
                const isOpen = open === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className="cursor-pointer border-t border-rule hover:bg-bg"
                      onClick={() => setOpen(isOpen ? null : r.id)}
                      aria-expanded={isOpen}
                    >
                      <td className="px-3 py-2 font-mono text-ink">
                        {r.registry}:{r.name}@{r.version}
                      </td>
                      <td className="px-3 py-2 text-dim">
                        {new Date(r.indexed_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {m.files.indexed}/{m.files.collected}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{filteredTotal(m)}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {share === null ? '—' : `${Math.round(share * 100)}%`}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono ${rate !== null && rate > FAILURE_RATE_ALERT ? 'font-semibold text-del' : ''}`}
                      >
                        {rate === null ? '—' : `${Math.round(rate * 100)}%`}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {m.docs
                          ? `${m.docs.pagesWritten}/${m.docs.inScope || m.docs.pagesWritten}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {m.retrieval
                          ? `${m.retrieval.vectors} (${pct(m.retrieval.prunedNodes, m.retrieval.nodes)} pruned)`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">${m.llm.cost.toFixed(3)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-rule bg-bg">
                        <td colSpan={9}>
                          <Detail m={m} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-rule px-3 py-2 text-xs text-dim">
              <span>
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, total)} of{' '}
                {total.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || isLoading}
                >
                  ← Prev
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={page >= lastPage || isLoading}
                >
                  Next →
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
